import { UseDropdownItems } from "@/common/composition/dropdown-items";
import { UseLoadingAction } from "@/common/composition/loading";
import { ScanSession } from "@/views/customize/data-sources/data/model";
import { state as data_sources_state } from "@/views/customize/data-sources/data/state";
import { Ref, computed, ref } from "@vue/composition-api";
import { FilesScanItem, PermissionCounter, FileScanStatus } from "./models";
import * as api from "./api";
import { UserSettings } from "@/common/api/user-settings.api";
import { SharedState } from "@/common/state";
import { delayBy, is_eq_by_props } from "@/fp";
import { UseModal } from "@/components/root/modal";
import { ToastType, UseToast } from "@/components/root/toast/toast.state";
import { UseCollection } from "@/common/composition/use-collection";
import { get_finding_entities } from "@/common/api/findings/findings.api";

const [, set_loading, is_loading] = UseLoadingAction<
  "init" | "change_session" | "load_files_scan_results"
>(false, "init");

const show_permission_cards_loading = is_loading("init", "change_session");
const show_files_scan_loader = is_loading("load_files_scan_results");

const PermissionsLabelsSettings = UserSettings.key<string[]>(
  "sp_site_permission",
  () => SharedState.l0_known_content_info_types.value.map((d) => d.id)
);

const selected_labels: Ref<string[]> = ref([]);
const selected_labels_count = computed(() => selected_labels.value.length);
const save_selected_labels_ids = async (ids: string[]) => {
  selected_labels.value = ids;
  await load_items();
  await PermissionsLabelsSettings.update(ids);
};

const [
  sessions,
  selected_session,
  _set_selected_session,
  init_sessions,
] = UseDropdownItems<ScanSession>([]);

const set_selected_session = async (session: ScanSession) => {
  // @ts-ignore
  _set_selected_session(session);
  selected_permission_card.value = null;
  set_loading(true, "change_session");
  const permission_counters_res = await api.get_sp_permissions_counts_and_status(
    +selected_session.value!.id
  );
  if (permission_counters_res.success) {
    permission_counters.value = permission_counters_res.value;
  }
  set_loading(false, "change_session");
  const first_counter_that_was_already_file_scanned = permission_counters.value.find(
    (f) => f.files_scan_status == "done"
  );
  if (first_counter_that_was_already_file_scanned) {
    view_scan_results(first_counter_that_was_already_file_scanned);
  }
};

const permission_counters: Ref<PermissionCounter[]> = ref([]);
const files_scan_items: Ref<FilesScanItem[]> = ref([]);
const selected_permission_card: Ref<PermissionCounter | null> = ref(null);

const load_items = async () => {
  set_loading(true, "load_files_scan_results");
  await delayBy(2000);
  const files_scan_result = await api.get_files_scan_results({
    scan: selected_session.value!,
    type: selected_permission_card.value!.type.main,
    sub_type: selected_permission_card.value!.type.sub_type,
    labels: selected_labels.value.map(
      (id) => SharedState.allInfoTypes.value[id]!
    ),
  });
  if (files_scan_result.success) {
    files_scan_items.value = files_scan_result.value;
  }
  set_loading(false, "load_files_scan_results");
};

const start_scan = async (item: PermissionCounter) => {
  const res = await UseModal().showSimple({
    title: "Sharepoint sites files scan",
    desc: `you are going to start scanning the files
   of <b>${item.count} sites</b> <br/>
      which had permission status of <b>${item.type.title}</b>
    `,
    cancelBtnText: "Cancel",
    submitBtnText: "Let's Scan",
  });
  if (res.type == "success") {
    UseToast().show({
      type: ToastType.SUCCESS,
      msg: "We're initiating the scan",
      duration: 3000,
    });
    update_counters_status(item, "in_progress");
  }
};

const update_counters_status = (
  item: PermissionCounter,
  status: FileScanStatus
) => {
  permission_counters.value = permission_counters.value.map((d) => {
    if (
      d.type.main == item.type.main &&
      d.type.sub_type == item.type.sub_type
    ) {
      return {
        ...d,
        files_scan_status: status,
      };
    }
    return d;
  });
};

const is_same_cards = is_eq_by_props(["main", "sub_type"]);
const view_scan_results = async (item: PermissionCounter) => {
  const is_same_card =
    selected_permission_card.value &&
    is_same_cards(selected_permission_card.value!.type, item.type);
  if (!is_same_card) {
    selected_permission_card.value = item;
    load_items();
  }
};

const sites_collection = UseCollection<any, { cursor: string }>(
  async ({ cursor }) => {
    return await get_finding_entities("SharepointWebsite")({
      // @ts-ignore
      application: "msft_sharepoint_365",
      service: "sp_site_permission",
      label_ids: [selected_permission_card.value!.id],
      type: "sp_site_permission",
      axis_z: {
        type: "Session",
        value: selected_session.value!.id,
      },
      cursor,
    });
  }
);

const show_websites_for_permission_status_card = async (
  item: PermissionCounter
) => {
  sites_collection.clear();
  selected_permission_card.value = item;
  await sites_collection.load_items({ cursor: "" });
};

const init = async (force = false) => {
  set_loading(true, "init");
  selected_labels.value = await PermissionsLabelsSettings.get();
  selected_labels.value = selected_labels.value.filter(
    (id) => SharedState.allInfoTypes.value[id]
  );
  await data_sources_state.init();
  const res = await data_sources_state.ext.get_sessions_for_source(
    "sp_site_permission"
  );
  if (res.success) {
    init_sessions([...res.value]);
    console.log(res.value);

    if (sessions.value.length > 0) {
      if (force) {
        set_selected_session(sessions.value[0]);
      } else {
        set_selected_session(selected_session.value || sessions.value[0]);
      }
    }
  }
  set_loading(false, "init");
};

export const state = {
  show_permission_cards_loading,
  show_files_scan_loader,
  init,
  sessions,
  selected_session,
  set_selected_session,
  permission_counters,
  files_scan_items,
  selected_labels,
  selected_labels_count,
  save_selected_labels_ids,
  start_scan,
  view_scan_results,
  selected_permission_card,
  // sites
  sites_collection,
  show_websites_for_permission_status_card,
};
